<template>
  <v-card class="d-flex flex-column flex" style="height: 100%">
    <v-card-title class="d-flex align-center">
      <v-btn icon class="mr-3" @click="$emit('back')">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span>{{ timetable ? timetable.name : 'Irregular Timetable' }}</span>
    </v-card-title>
    <v-card-subtitle>
      <div class="d-flex justify-between px-4">
        <div>
          <div>
            {{ timetable.programme ? timetable.programme.name : '' }} {{ timetable.semester ? timetable.semester.title : '' }} - Irregular Schedule
          </div>
          <div class="d-flex align-center">
            <span>Today is {{ getCurrentDate() }}</span>
            <v-btn class="ml-2" small icon color="primary" title="Print Timetable" @click="printTimetable">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="ml-auto">
          <v-btn depressed color="primary" @click="isCreateTableItem = true">Create Timetable Item</v-btn>
        </div>
      </div>
    </v-card-subtitle>

    <v-card-text>
      <div class="timetable-container">
        <!-- Calendar View -->
        <v-sheet class="calendar-view">
          <template v-if="Object.keys(timetableItems).length === 0">
            <v-alert type="info" text>
              No timetable items scheduled yet.
            </v-alert>
          </template>
          <template v-else>
            <div v-for="date in sortedDates" :key="date" class="date-section">
              <v-card class="mb-4">
                <v-card-title 
                  class="py-2 white--text" 
                  :class="{
                    'red': isToday(date),
                    'amber darken-2': isNextDate(date),
                    'primary': !isToday(date) && !isNextDate(date)
                  }">
                  {{ formatDateHeader(date) }}
                </v-card-title>
                <v-card-text class="py-2">
                  <div class="items-grid">
                    <v-hover v-for="(item, index) in timetableItems[date]" :key="index" v-slot="{ hover }">
                      <v-card :elevation="hover ? 4 : 1" :class="{'on-hover': hover}" class="grid-item">
                        <div class="pa-3 h-100 d-flex flex-column">
                          <div class="d-flex justify-space-between align-center">
                            <div class="font-weight-bold primary--text text-truncate">{{ item.title }}</div>
                            <div v-if="hover" class="d-flex">
                              <v-btn icon x-small color="primary" class="edit-btn mr-1" @click.stop="editTimetableItem(item)">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                              <v-btn icon x-small color="error" class="delete-btn" @click.stop="confirmDeleteItem(item)">
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </div>
                          <div class="d-flex align-center mt-1">
                            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                            <span class="time-range text-truncate">{{ formatTimeRange(item) }}</span>
                          </div>
                          <div class="d-flex align-center mt-1" v-if="item.lecturer">
                            <v-icon small class="mr-1">mdi-account</v-icon>
                            <span class="lecturer text-truncate">{{ item.lecturer }}</span>
                          </div>
                          <div class="d-flex align-center mt-1" v-if="item.venue">
                            <v-icon small class="mr-1">mdi-map-marker</v-icon>
                            <span class="venue text-truncate">{{ item.venue }}</span>
                          </div>
                        </div>
                      </v-card>
                    </v-hover>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </template>
        </v-sheet>
      </div>
    </v-card-text>

    <!-- Add timetable item dialog -->
    <v-dialog v-model="isCreateTableItem" width="600px">
      <v-card>
        <v-card-title class="d-flex">
          {{ editMode ? 'Edit' : 'Add' }} Time Table Item
          <v-btn class="ml-auto" depressed text color="error" @click="isCreateTableItem = false; editMode = false; tbItemObj = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Title of the table item-->
          <v-text-field 
            dense 
            placeholder="Enter Name" 
            outlined 
            v-model="tbItemObj.title" 
          />

          <!-- select date -->
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="tbItemObj.date"
                dense
                label="Select Date"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="tbItemObj.date"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>

          <!-- start Time -->
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="tbItemObj.startTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="tbItemObj.startTime"
                label="Start Time"
                prepend-inner-icon="mdi-clock-time-four-outline"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="menu2"
              v-model="tbItemObj.startTime"
              full-width
              @click:minute="$refs.menu2.save(tbItemObj.startTime)"
            ></v-time-picker>
          </v-menu>

          <!-- stop Time -->
          <v-menu
            ref="menu3"
            v-model="menu3"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="tbItemObj.stopTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="tbItemObj.stopTime"
                label="Stop Time"
                prepend-inner-icon="mdi-clock-time-four-outline"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="menu3"
              v-model="tbItemObj.stopTime"
              full-width
              @click:minute="$refs.menu3.save(tbItemObj.stopTime)"
            ></v-time-picker>
          </v-menu>

          <!-- Venue -->
          <v-text-field
            dense
            placeholder="Enter Venue"
            outlined
            v-model="tbItemObj.venue"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            depressed
            color="primary"
            style="width: 40%;"
            class="d-flex mx-auto"
            large
            @click="saveTimetableItem"
          >
            {{ editMode ? 'Update' : 'Add' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="isDeleteDialogOpen" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete Timetable Item</v-card-title>
        <v-card-text>
          Are you sure you want to delete this timetable item?
          <div v-if="itemToDelete" class="mt-2">
            <strong>{{ itemToDelete.title }}</strong>
            <div v-if="itemToDelete.date">{{ formatDateHeader(itemToDelete.date) }} ({{ formatTimeRange(itemToDelete) }})</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="isDeleteDialogOpen = false">Cancel</v-btn>
          <v-btn color="error" text @click="deleteTimetableItem">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <loading-dialog :show="loading"></loading-dialog>
  </v-card>
</template>

<script>
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import timeTable from "@/shared/apis/timetable/timetable";
import LoadingDialog from "@ui/components/LoadingDialog";

export default {
  name: 'IrregularTimetableDetailsView',
  
  components: {
    LoadingDialog
  },
  
  props: {
    timetable: {
      type: Object,
      default: null
    }
  },
  
  data: () => ({
    menu: false,
    menu2: false,
    menu3: false,
    timetableItems: {},
    loading: false,
    error: null,
    isCreateTableItem: false,
    isDeleteDialogOpen: false,
    itemToDelete: null,
    editMode: false,
    currentItemId: null,
    
    tbItemObj: {
      title: '',
      date: null,
      startTime: '08:00',
      stopTime: '10:00',
      lecturer: '',
      venue: '',
      timeTableId: null
    }
  }),
  
  methods: {
    printTimetable() {
      // Store current body styles
      const originalBodyStyle = document.body.style.cssText;
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Generate print content
      const content = `
        <html>
          <head>
            <title>${this.timetable ? this.timetable.name : 'Irregular Timetable'}</title>
            <style>
              body { font-family: Arial, sans-serif; }
              .timetable { width: 100%; border-collapse: collapse; margin-top: 20px; }
              .timetable th, .timetable td { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: left; 
              }
              .timetable th { 
                background-color: #1976D2; 
                color: white; 
              }
              .date-header {
                background-color: #1976D2;
                color: white;
                padding: 8px;
                margin-top: 20px;
                border-radius: 4px;
              }
              .course-item { 
                margin: 5px 0; 
                padding: 8px; 
                background-color: #f5f5f5; 
                border-radius: 4px; 
              }
              .header { 
                text-align: center; 
                margin-bottom: 20px; 
              }
              @media print {
                .date-section { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>${this.timetable ? this.timetable.name : 'Irregular Timetable'}</h2>
              <p>${this.timetable.programme ? this.timetable.programme.name : ''} ${this.timetable.semester ? this.timetable.semester.title : ''} - Irregular Schedule</p>
              <p>Generated on ${this.getCurrentDate()}</p>
            </div>
            ${this.sortedDates.map(date => `
              <div class="date-section">
                <div class="date-header">
                  ${this.formatDateHeader(date)}
                </div>
                ${this.timetableItems[date].map(item => `
                  <div class="course-item">
                    <strong>${item.title}</strong><br>
                    Time: ${this.formatTimeRange(item)}<br>
                    ${item.lecturer ? `Lecturer: ${item.lecturer}<br>` : ''}
                    ${item.venue ? `Venue: ${item.venue}` : ''}
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </body>
        </html>
      `;

      // Write content to the new window
      printWindow.document.write(content);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print();
        printWindow.onafterprint = function() {
          printWindow.close();
          // Restore original body styles
          document.body.style.cssText = originalBodyStyle;
        };
      };
    },
    getCurrentDate() {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    },

    formatDateHeader(dateStr) {
      try {
        const date = new Date(dateStr);
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
      } catch (e) {
        console.error('Error formatting date:', e);
        return dateStr;
      }
    },
    
    isToday(dateStr) {
      try {
        console.log('Checking if date is today:', dateStr);
        
        // Parse the date string
        let itemDate;
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
          // Handle ISO format (YYYY-MM-DD)
          itemDate = new Date(dateStr);
        } else {
          // Try direct parsing
          itemDate = new Date(dateStr);
        }
        
        const today = new Date();
        
        console.log('Item date:', itemDate);
        console.log('Today:', today);
        
        // Set both dates to midnight for comparison
        itemDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        // Compare year, month, and day
        const isToday = itemDate.getFullYear() === today.getFullYear() &&
                        itemDate.getMonth() === today.getMonth() &&
                        itemDate.getDate() === today.getDate();
                        
        console.log('Is today?', isToday);
        return isToday;
      } catch (e) {
        console.error('Error checking if date is today:', e, dateStr);
        return false;
      }
    },
    
    isNextDate(dateStr) {
      try {
        // Get all dates from timetable items and convert to Date objects
        const dates = Object.keys(this.timetableItems).map(date => {
          let dateObj;
          try {
            dateObj = new Date(date);
            // Check if date is valid
            if (isNaN(dateObj.getTime())) {
              console.warn('Invalid date:', date);
              return null;
            }
          } catch (err) {
            console.warn('Error parsing date:', date, err);
            return null;
          }
          
          return {
            dateStr: date,
            date: dateObj
          };
        }).filter(item => item !== null); // Remove invalid dates
        
        // Get today's date at midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTime = today.getTime();
        
        // Find all future dates
        const futureDates = dates.filter(d => d.date.getTime() > todayTime);
        
        // Sort future dates chronologically
        futureDates.sort((a, b) => a.date.getTime() - b.date.getTime());
        
        // If there are future dates, check if the current date is the next one
        if (futureDates.length > 0) {
          const nextDate = futureDates[0].dateStr;
          const result = dateStr === nextDate;
          
          // Debug
          if (result) {
            console.log('Next date found:', dateStr);
          }
          
          return result;
        }
        
        return false;
      } catch (e) {
        console.error('Error checking if date is next:', e, dateStr);
        return false;
      }
    },

    formatTimeRange(item) {
      if (!item || !item.startTime || !item.stopTime) return '';
      
      const format12Hour = (timeArray) => {
        if (!Array.isArray(timeArray) || timeArray.length < 2) return '';
        const hours = timeArray[0];
        const minutes = timeArray[1];
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
      };
      
      return `${format12Hour(item.startTime)} - ${format12Hour(item.stopTime)}`;
    },

    convertTimeToDateTime(time) {
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
    },

    resetForm() {
      this.tbItemObj = {
        title: '',
        date: null,
        startTime: '08:00',
        stopTime: '10:00',
        lecturer: '',
        venue: '',
        timeTableId: this.timetable ? this.timetable.id : null
      };
    },

    editTimetableItem(item) {
      if (!item) return;
      
      this.editMode = true;
      this.resetForm();
      this.currentItemId = item.id;
      
      this.tbItemObj = {
        title: item.title || '',
        date: item.date || null,
        lecturer: item.lecturer || '',
        venue: item.venue || '',
        timeTableId: this.timetable.id
      };
      
      if (item.startTime && Array.isArray(item.startTime)) {
        this.tbItemObj.startTime = `${item.startTime[0].toString().padStart(2, '0')}:${item.startTime[1].toString().padStart(2, '0')}`;
      }
      
      if (item.stopTime && Array.isArray(item.stopTime)) {
        this.tbItemObj.stopTime = `${item.stopTime[0].toString().padStart(2, '0')}:${item.stopTime[1].toString().padStart(2, '0')}`;
      }
      
      this.isCreateTableItem = true;
    },

    saveTimetableItem() {
      this.loading = true;

      const payload = {
        ...this.tbItemObj,
        timeTableId: this.timetable.id,
        startTime: this.convertTimeToDateTime(this.tbItemObj.startTime),
        stopTime: this.convertTimeToDateTime(this.tbItemObj.stopTime)
      };

      const apiCall = this.editMode && this.currentItemId
        ? Api.timetable.updateTimetableItem(this.currentItemId, payload)
        : Api.timetable.createTimetableItem(payload);

      apiCall
        .then(() => {
          this.$notify({
            type: 'success',
            message: `Time Table Item ${this.editMode ? 'updated' : 'created'} successfully`
          });
          this.isCreateTableItem = false;
          this.loadTimetableItems();
        })
        .catch((error) => ErrorResolver.checkErrors(this, error))
        .finally(() => {
          this.loading = false;
        });
    },
    
    confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.isDeleteDialogOpen = true;
    },
    
    deleteTimetableItem() {
      if (!this.itemToDelete || !this.itemToDelete.id) {
        this.$notify({
          type: 'error',
          message: 'Invalid timetable item'
        });
        return;
      }
      
      this.loading = true;
      
      Api.timetable.deleteTimetableitem(this.itemToDelete.id)
        .then(() => {
          this.$notify({
            type: 'success',
            message: `Timetable item deleted successfully`
          });
          this.loadTimetableItems();
          this.isDeleteDialogOpen = false;
          this.itemToDelete = null;
        })
        .catch((error) => ErrorResolver.checkErrors(this, error))
        .finally(() => {
          this.loading = false;
        });
    },

    loadTimetableItems() {
      if (!this.timetable || !this.timetable.id) return;
      
      this.loading = true;
      this.error = null;
      
      Api.timetable.getTimeTableItems(this.timetable.id)
        .then(response => {
          this.timetableItems = response.data || {};
        })
        .catch(error => {
          this.error = 'Failed to load timetable items';
          ErrorResolver.checkErrors(this, error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  
  computed: {
    sortedDates() {
      return Object.keys(this.timetableItems).sort((a, b) => {
        // Convert date strings to Date objects for comparison
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA - dateB; // Sort in ascending order (oldest to newest)
      });
    }
  },
  
  watch: {
    timetable: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadTimetableItems();
        } else {
          this.timetableItems = {};
        }
      }
    }
  }
}
</script>

<style scoped>
.timetable-container {
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-view {
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.date-section {
  margin-bottom: 16px;
}

.date-section:last-child {
  margin-bottom: 0;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  padding: 8px;
}

.grid-item {
  height: 100%;
  min-height: 120px;
  transition: all 0.3s;
  border-left: 4px solid var(--v-primary-base);
}

.grid-item.on-hover {
  background-color: #f9f9f9;
}

.time-range, .lecturer, .venue {
  font-size: 0.8rem;
  color: #757575;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.edit-btn {
  opacity: 0.8;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .items-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .items-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1264px) {
  .items-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style> 